import { load } from 'cheerio';
import MarkdownIt from 'markdown-it';
import React, { useContext, useEffect } from 'react';
import { useStreamedTextQuery } from '../../../../api/ApiStreaming';
import { CommandDispatcher } from '../../../../concepts/commands/CommandDispatcher';
import { Button } from '../../../../ui';
import { EditorSession } from '../../../canvas/EditorSession';
import { Physical } from '../../../canvas/entities/Physical';
import { Selection } from '../../../canvas/Selection';
import { useResolve } from '../../../Container';
import { Copy } from '../../../dashboard/elements/LayoutElements';
import { jiraCreateIssue } from '../../atlassian/AtlassianCommands';
import { Graphic } from '../../graphics/Graphic';
import { AssistantContext } from '../AssistantButtonWidget';
import { Chat } from '../Chat';

const md = new MarkdownIt('default', { linkify: true });

const stripCodeBlock = (text: string) => {
    text = text.trim();
    if (text.startsWith('```markdown')) text = text.slice(11);
    if (text.startsWith('```')) text = text.slice(3);
    if (text.endsWith('```')) text = text.slice(0, -3);
    return text.trim();
};

const toMessageParts = (p: Physical) => {
    if (p.is(Graphic)) {
        return [
            { type: 'image', value: p.get('url') },
            { type: 'text', value: p.get('url') },
        ];
    }
    else {
        return [{ type: 'text', value: p.toText() }];
    }
};

const atlassifyLinks = (text: string): string => {
    // Match both regular links and image embeds, using regular capture groups
    const linkRegex = /(?<!\\)(!?\[([^\]]*)\]\(([^\)]+)\))/g;

    // Replace each match with Jira format [text:url]
    return text.replace(linkRegex, (_match, _full, text, url) => {
        return `[${text}|${url}]`;
    });
};

const inspectReply = (text: string) => {

    const $ = load(md.render(stripCodeBlock(text)));

    let title = '';
    let issueType = '';
    let summary = '';
    let rest = '';

    $('p').each((_, element) => {
        const t = $(element).text();
        if (t.startsWith('Title:')) {
            title = t.split(/\:/g)?.[1]?.trim();
        }
        else if (t.startsWith('Issue Type:')) {
            issueType = t.split(/\:/g)?.[1]?.trim();
        }
        else if (t.startsWith('Summary:')) {
            summary = t.split(/\:/g)?.[1]?.trim();
        }
    });

    if (summary) {
        rest = stripCodeBlock(text).split(summary)[1]?.trim()
            .replace(/\*\*/g, '*');
    }

    const description = atlassifyLinks(`${summary}\n\n${rest}`);

    return {
        title,
        issueType,
        description,
    };
};

export const JiraCreateInteraction = ({ input }: { input: string }) => {

    const { state, onSubmit } = useContext(AssistantContext);
    const dispatch = useResolve(CommandDispatcher);
    const session = useResolve(EditorSession);
    const [reply, triggerReply, done] = useStreamedTextQuery<{ code: string, message: any }>('/llm.invokeCapability');

    // Start
    useEffect(() => {

        const selection = session.get(Selection);
        const message = [
            { type: 'text', value: 'Here is the users selected objects' },
            ...Array.from(selection).flatMap(toMessageParts),
            { type: 'text', value: input },
        ];

        triggerReply({ code: 'jira_create', message });
    }, []);

    // Tick
    useEffect(() => {
        state.mutate(x => void (x.tick++));
    }, [reply, done]);

    const onSave = () => {
        const { issueType, title, description } = inspectReply(reply);
        dispatch(jiraCreateIssue, [issueType, title, description]);
    };

    return <Chat.Block actor={<Chat.Seb />}>
        <Chat.Message>
            {reply && <Copy flush dangerouslySetInnerHTML={{ __html: md.render(stripCodeBlock(reply)) }} />}
            {!reply && <Copy>Using Jira Create capability...</Copy>}
        </Chat.Message>
        {done &&
            <Chat.Actions>
                <Button onAction={onSave}>
                    Save to Jira
                </Button>
                <Button onAction={() => onSubmit(input)}>
                    Repeat
                </Button>
            </Chat.Actions>
        }
    </Chat.Block>;
};
